import { Card, Intent, OverlayToaster } from "@blueprintjs/core";
import { signIn } from "next-auth/react";
import { useState } from "react";
import HomepageAccountForm from "../../components/login/accountform";
import HomepageBottomText from "../../components/login/bottomtext";
import { homepageForgotPassword, homepageRegister } from "../../src/thunks/homepage";
import styles from './accountform.module.css';
import { validateUserRegistrationParams } from "../../src/models/user/auth";
import { useToaster } from "../global/toaster";
import { HomepageStatus } from "./homepagestatus";

interface HomepageAccountFormContainerProps {
    status?: HomepageStatus;
    successPath?: string;
    error?: string;
};

export default function HomepageAccountFormContainer(
    {
        error,
        status,
        successPath,
    }: HomepageAccountFormContainerProps,
) {
    const toaster = useToaster();

    const [formStatus, setFormStatus] = useState(status ?? HomepageStatus.Login);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [isSubmitDisabled, isSetSubmitDisabled] = useState(false);

    const onSubmit = async () => {
        isSetSubmitDisabled(true);

        const fn = (formStatus === HomepageStatus.Login) ? onLogin : onRegister;

        try {
            await fn(email, password, name);
        } catch (err) {
            console.error(`Error`, err);

            toaster.show({
                intent: Intent.DANGER,
                message: `Error: ${err.message}`,
            });
        } finally {
            isSetSubmitDisabled(false);
        }
    }

    const onLogin = async (loginEmail, loginPassword) => {
        const response = await signIn(
            'credentials',
            {
                email: loginEmail,
                password: loginPassword,
                callbackUrl: `${successPath ?? window.location.origin}`,
            },
        );

        return response;
    }

    const onRegister = async (registerEmail, registerPassword, registerName) => {
        const errorMessage = validateUserRegistrationParams(
            registerEmail,
            registerPassword,
            registerName,
        );

        if (errorMessage) {
            throw new Error(errorMessage);
        }

        const response = await homepageRegister(registerEmail, registerPassword, registerName);

        if (response.status === "error") {
            throw new Error(response.message);
        } else {
            setFormStatus(HomepageStatus.Login);

            toaster.show({
                intent: Intent.SUCCESS,
                message: 'Account created! Now log in.',
            });
        }
    }

    const forgotPassword = async () => {
        if (email.length === 0) {
            toaster.show({
                intent: Intent.DANGER,
                message: 'Please enter your email address',
            });

            return;
        }

        isSetSubmitDisabled(true);
        let success = false;

        try {
            const response = await homepageForgotPassword(email);

            if (response.status !== 'ok') {
                console.error('bad response:', response);
                throw new Error();
            }

            success = true;
        } catch (err) {
            toaster.show({
                intent: Intent.DANGER,
                message: 'Error recovering account, please try again later',
            });
        } finally {
            isSetSubmitDisabled(false);
        }

        if (success) {
            toaster.show({
                intent: Intent.SUCCESS,
                message: 'Success! Please check your email to reset your password',
            });
        }
    }

    const onToggleStatus = () => {
        setFormStatus(
            formStatus === HomepageStatus.Login
                ? HomepageStatus.RegisterAccount
                : HomepageStatus.Login,
        );
    };

    const buttonText = formStatus === HomepageStatus.Login
        ? "Log in"
        : "Register";

    return (
        <Card>
            <h1>{formStatus === HomepageStatus.Login ? 'Login' : 'Register'}</h1>

            {error && <h3 className={styles.error}>{error}</h3>}

            <HomepageAccountForm
                buttonText={buttonText}
                email={email}
                hideName={formStatus === HomepageStatus.Login}
                isSubmitDisabled={isSubmitDisabled}
                name={name}
                onSubmit={onSubmit}
                password={password}
                setEmail={setEmail}
                setName={setName}
                setPassword={setPassword}
                forgotPassword={forgotPassword}
            />

            <HomepageBottomText
                status={formStatus}
                onClick={onToggleStatus}
            />
        </Card>
    );
}
